import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Gavel, Loader2, Wand2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { JudgeOptionLabel } from "./JudgeOptionLabel";

// Inline ορισμός Δικαστή Έδρας για ένα πινάκιο × δικάσιμο (Πινάκια, Ημερολόγιο Έδρας)
export const BenchJudgePicker = ({ pinakio, dateGroup, judges, onDone, hideBadge = false }) => {
  const [open, setOpen] = useState(false);
  const [judgeName, setJudgeName] = useState("");
  const [busy, setBusy] = useState(false);
  const ct = dateGroup?.cases?.[0]?.court_type || pinakio.court_type || "Μονομελές";
  const tid = `${pinakio.pinakio_code}-${dateGroup.date}`;
  const current = dateGroup.bench_judge || "";
  const multi = (dateGroup.bench_judges || []).length > 1;
  const suggestion = dateGroup.pretrial_judge_suggestion || "";
  const manyPretrial = (dateGroup.pretrial_judges || []).length > 1;

  const call = async (body) => {
    try {
      setBusy(true);
      const r = await axios.post(`${API}/hearings/bench-judge`, {
        pinakio_label: pinakio.label, hearing_date: dateGroup.date, court_type: ct, ...body,
      });
      toast.success(r.data?.message || "Ενημερώθηκε ο Δικαστής Έδρας");
      setOpen(false); setJudgeName("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ορισμού Δικαστή Έδρας", { duration: 12000 });
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="flex items-center gap-1">
        {(current || multi) && !hideBadge && (
          <Badge variant="outline" className="h-6 text-[11px] border-amber-400 text-amber-800 bg-amber-50 gap-1"
                 data-testid={`bench-judge-badge-${tid}`} title="Δικαστής Έδρας">
            <Gavel className="w-3 h-3" />
            {multi ? `Έδρα: ${dateGroup.bench_judges.length} δικαστές` : `Έδρα: ${current}`}
          </Badge>
        )}
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-amber-700 hover:text-amber-900"
                onClick={() => { setOpen(true); setJudgeName(current || suggestion || ""); }}
                data-testid={`bench-judge-${tid}`}>
          <Gavel className="w-3.5 h-3.5" />
          {current ? "Αλλαγή Δικ. Έδρας" : "Δικαστής Έδρας"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 flex-wrap">
        <Select value={judgeName} onValueChange={setJudgeName}>
          <SelectTrigger className="h-7 w-64 text-xs" data-testid={`bench-judge-select-${tid}`}>
            <SelectValue placeholder={judges.length ? `Δικαστής Έδρας (${ct})` : "Δεν υπάρχουν δικαστές"} />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {judgeName && !judges.some(j => j.full_name === judgeName) && (
              <SelectItem value={judgeName}>{judgeName}</SelectItem>
            )}
            {judges.map(j => (
              <SelectItem key={j.id} value={j.full_name} data-testid={`bench-judge-option-${tid}-${j.id}`}>
                <JudgeOptionLabel judge={j} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-amber-400 text-amber-800"
                disabled={busy || !suggestion} onClick={() => call({ from_pretrial: true })}
                title={manyPretrial ? `Πολλοί δικαστές προδικαστικού: ${dateGroup.pretrial_judges.join(", ")}` : (suggestion ? `Ορισμός: ${suggestion}` : "Δεν υπάρχει δικαστής προδικαστικού")}
                data-testid={`bench-judge-from-pretrial-${tid}`}>
          <Wand2 className="w-3 h-3" /> Από Προδικαστικό
        </Button>
        <Button size="sm" className="h-7 text-xs" disabled={busy || !judgeName}
                onClick={() => call({ judge_name: judgeName })} data-testid={`bench-judge-confirm-${tid}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}
        </Button>
        {current && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" disabled={busy} title="Αφαίρεση Δικαστή Έδρας"
                  onClick={() => call({ clear: true })} data-testid={`bench-judge-clear-${tid}`}>
            <XCircle className="w-3 h-3" />
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setOpen(false); setJudgeName(""); }}
                data-testid={`bench-judge-cancel-${tid}`}>
          &#x2715;
        </Button>
      </div>
      {manyPretrial && (
        <p className="text-[11px] text-orange-700 ml-1" data-testid={`bench-judge-multi-warning-${tid}`}>
          Πολλοί δικαστές προδικαστικού στη δικάσιμο ({dateGroup.pretrial_judges.join(", ")}) — επιλέξτε χειροκίνητα.
        </p>
      )}
    </div>
  );
};

export default BenchJudgePicker;
